import React, { useEffect, useRef, useState, useCallback } from "react";
import moment, { Moment } from "moment";
import "moment/locale/ar";
import i18n from "../../../../../../../i18n/i18n";
import styles from "./styles.module.sass";

const getDateLocale = () => (i18n.language?.includes("ar") ? "ar" : "en");

const getWeekdayHeaders = () => {
  const localeData = moment.localeData(getDateLocale());
  const minWeekdays = localeData.weekdaysMin();
  const firstDayOfWeek = localeData.firstDayOfWeek();
  // The grid is always rendered Sunday-first (JS day 0-6), so rotate the
  // locale-ordered weekday names to align with it regardless of locale dow.
  return Array.from(
    { length: 7 },
    (_, jsDay) => minWeekdays[(jsDay - firstDayOfWeek + 7) % 7].charAt(0)
  );
};

interface CalendarDay {
  date: Moment;
  isCurrentMonth: boolean;
}

function generateCalendarDays(displayMonth: Moment): CalendarDay[] {
  const startOfMonth = displayMonth.clone().startOf("month");
  const endOfMonth = displayMonth.clone().endOf("month");
  const firstDayOfWeek = startOfMonth.day();

  const days: CalendarDay[] = [];

  for (let i = firstDayOfWeek - 1; i >= 0; i--) {
    days.push({ date: startOfMonth.clone().subtract(i + 1, "days"), isCurrentMonth: false });
  }

  for (let d = 1; d <= endOfMonth.date(); d++) {
    days.push({ date: displayMonth.clone().startOf("month").date(d), isCurrentMonth: true });
  }

  let nextDay = 1;
  while (days.length < 42) {
    days.push({ date: endOfMonth.clone().add(nextDay, "days"), isCurrentMonth: false });
    nextDay++;
  }

  return days;
}

function toHour12(h24: number): number {
  return h24 % 12 || 12;
}

function toPeriod(h24: number): "AM" | "PM" {
  return h24 >= 12 ? "PM" : "AM";
}

function toHour24(h12: number, period: "AM" | "PM"): number {
  if (period === "AM") return h12 === 12 ? 0 : h12;
  return h12 === 12 ? 12 : h12 + 12;
}

interface CustomDateTimePickerProps {
  value: Moment | null;
  onApply: (value: Moment) => void;
  onCancel: () => void;
  requireFuture?: boolean;
  minDate?: Moment | null;
  cancelLabel?: string;
  applyLabel?: string;
}

interface TimeInputProps {
  value: number;
  onChange: (val: number) => void;
  min: number;
  max: number;
}

const TimeInput: React.FC<TimeInputProps> = ({ value, onChange, min, max }) => {
  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    if (raw === "") {
      onChange(min);
      return;
    }
    const num = parseInt(raw, 10);
    if (!isNaN(num)) {
      if (num >= min && num <= max) {
        onChange(num);
      } else if (num > max) {
        onChange(max);
      }
    }
  }, [min, max, onChange]);

  const handleBlur = useCallback(() => {
    // Ensure value is always displayed with 2 digits
  }, []);

  return (
    <input
      type="number"
      className={styles.timeInput}
      value={value}
      onChange={handleChange}
      onBlur={handleBlur}
      min={min}
      max={max}
    />
  );
};

const resolvePickerFloor = (requireFuture: boolean, minDate?: Moment | null): Moment | null => {
  const nowFloor = requireFuture ? moment() : null;
  if (minDate && nowFloor) {
    return minDate.isAfter(nowFloor) ? minDate.clone() : nowFloor;
  }
  return minDate?.clone() || nowFloor;
};

const resolveInitDate = (
  value: Moment | null,
  floor: Moment | null
): Moment => {
  const fallback = value || floor || moment().endOf("day");
  if (floor && fallback.isBefore(floor)) {
    return floor.clone();
  }
  return fallback.clone();
};

const CustomDateTimePicker: React.FC<CustomDateTimePickerProps> = ({
  value,
  onApply,
  onCancel,
  requireFuture = true,
  minDate,
  cancelLabel = "Cancel",
  applyLabel = "Apply",
}) => {
  const floor = resolvePickerFloor(requireFuture, minDate);
  const init = resolveInitDate(value, floor);

  const [displayMonth, setDisplayMonth] = useState(() => init.clone().startOf("month"));
  const [selectedDate, setSelectedDate] = useState<Moment>(() => init.clone().startOf("day"));
  const [hour, setHour] = useState(() => toHour12(init.hour()));
  const [minute, setMinute] = useState(() => init.minute());
  const [period, setPeriod] = useState<"AM" | "PM">(() => toPeriod(init.hour()));
  const footerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const nextFloor = resolvePickerFloor(requireFuture, minDate);
    const d = resolveInitDate(value, nextFloor);
    setDisplayMonth(d.clone().startOf("month"));
    setSelectedDate(d.clone().startOf("day"));
    setHour(toHour12(d.hour()));
    setMinute(d.minute());
    setPeriod(toPeriod(d.hour()));
  }, [value, minDate, requireFuture]);

  const handleApply = () => {
    const result = selectedDate.clone();
    result.hour(toHour24(hour, period)).minute(minute).second(0);

    if (minDate && result.isBefore(minDate)) {
      return;
    }
    if (requireFuture && !result.isAfter(moment())) {
      return;
    }

    onApply(result);
  };

  const handleTogglePeriod = useCallback(() => {
    setPeriod((p) => (p === "AM" ? "PM" : "AM"));
  }, []);

  const calendarDays = generateCalendarDays(displayMonth);

  const canGoPrev = floor
    ? displayMonth.clone().startOf("month").isAfter(floor.clone().startOf("month"))
    : true;

  const now = floor || moment();
  const isToday = selectedDate.isSame(now, "day");
  const isAmDisabled = isToday && now.hour() >= 12;

  // If AM is disabled and currently AM, force switch to PM
  useEffect(() => {
    if (isAmDisabled && period === "AM") {
      setPeriod("PM");
    }
  }, [isAmDisabled, period]);

  return (
    <div className={styles.popover}>
      <div className={styles.calendarArea}>
        <div className={styles.calendarHeader}>
          <button
            type="button"
            className={styles.navBtn}
            disabled={!canGoPrev}
            onClick={() => setDisplayMonth((m) => m.clone().subtract(1, "month"))}
          >
            <svg width="8" height="14" viewBox="0 0 8 14" fill="none">
              <path d="M7 1L1 7L7 13" stroke="#1C1C1C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <span className={styles.monthLabel}>
            {displayMonth.locale(getDateLocale()).format("MMMM YYYY")}
          </span>
          <button
            type="button"
            className={styles.navBtn}
            onClick={() => setDisplayMonth((m) => m.clone().add(1, "month"))}
          >
            <svg width="8" height="14" viewBox="0 0 8 14" fill="none">
              <path d="M1 1L7 7L1 13" stroke="#1C1C1C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>

        <div className={styles.weekdayRow}>
          {getWeekdayHeaders().map((d, i) => (
            <div key={i} className={styles.weekdayCell}>{d}</div>
          ))}
        </div>

        <div className={styles.daysGrid}>
          {calendarDays.map((day, i) => {
            const isPast = !!floor && day.date.isBefore(floor, "day");
            return (
              <div
                key={i}
                className={[
                  styles.dayCell,
                  !day.isCurrentMonth ? styles.dayCellOutside : "",
                  day.date.isSame(selectedDate, "day") ? styles.dayCellSelected : "",
                  isPast ? styles.dayCellDisabled : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onClick={() => {
                  if (day.isCurrentMonth && !isPast) {
                    setSelectedDate(day.date.clone());
                    // The popover can be height-constrained; make sure the time
                    // picker and Cancel/Apply buttons are visible after picking a day.
                    footerRef.current?.scrollIntoView({
                      behavior: "smooth",
                      block: "nearest",
                    });
                  }
                }}
              >
                {day.date.date()}
              </div>
            );
          })}
        </div>
      </div>

      <div className={styles.timePicker}>
        <TimeInput value={hour} onChange={setHour} min={1} max={12} />
        <span className={styles.timeSeparator}>:</span>
        <TimeInput value={minute} onChange={setMinute} min={0} max={59} />
        <div className={styles.periodToggle}>
          <div
            className={`${styles.periodToggleIndicator} ${period === "PM" ? styles.periodToggleIndicatorRight : ""}`}
          />
          <button
            type="button"
            className={`${styles.periodToggleBtn} ${period === "AM" ? styles.periodToggleBtnActive : ""}`}
            onClick={() => setPeriod("AM")}
            disabled={isAmDisabled}
          >
            {moment.localeData(getDateLocale()).meridiem(9, 0, false)}
          </button>
          <button
            type="button"
            className={`${styles.periodToggleBtn} ${period === "PM" ? styles.periodToggleBtnActive : ""}`}
            onClick={() => setPeriod("PM")}
          >
            {moment.localeData(getDateLocale()).meridiem(15, 0, false)}
          </button>
        </div>
      </div>

      <div ref={footerRef} className={styles.pickerFooter}>
        <button type="button" className={styles.pickerCancelBtn} onClick={onCancel}>
          {cancelLabel}
        </button>
        <button type="button" className={styles.pickerApplyBtn} onClick={handleApply}>
          {applyLabel}
        </button>
      </div>
    </div>
  );
};

export default CustomDateTimePicker;
